import { createApi } from '@reduxjs/toolkit/query/react';

import {
  Blob,
  BlobCreateRequest,
  BlobDetail,
  BlobUpdateRequest,
  BlobWithMarkdownContent,
  BulkDownloadRequest,
  BulkReSortInitResponse,
  BulkReSortOperationSchema,
  BulkResortRequest,
  CompleteMultipartPayload,
  CompletePresignedUploadPayload,
  CopyBlobsToCompanionsRequest,
  CopyBlobsToCompanionsResponse,
  ExtractPagesRequest,
  ExtractPagesResponse,
  InitiateMultipartPayload,
  InitiateMultipartResponse,
  InitiateUploadPayload,
  InitiateUploadResponse,
  MoveBlobToMatterRequest,
  PresignPartPayload,
  PresignPartResponse,
  SortingRulesResponse,
} from '../types/blob-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const blobsApiClient = createApi({
  reducerPath: 'blobsApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Blobs', 'BlobDetail', 'ArtifactAudit'] as const,
  endpoints: (builder) => ({
    getBlobs: builder.query<Blob[], string>({
      query: (matter_id) => `api/matters/${matter_id}/blobs/`,
      providesTags: (result, error, matter_id) => [
        { type: 'Blobs', id: matter_id },
      ],
    }),
    createBlob: builder.mutation<
      Blob,
      { matterId: string; blob: BlobCreateRequest }
    >({
      query: ({ matterId, blob }) => ({
        url: `api/matters/${matterId}/blobs/`,
        method: 'POST',
        body: blob,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Blobs', id: matterId },
      ],
    }),
    destroyBlob: builder.mutation<void, { matterId: string; blobId: string }>({
      query: ({ matterId, blobId }) => ({
        url: `api/matters/${matterId}/blobs/${blobId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Blobs', id: matterId },
      ],
    }),
    updateBlob: builder.mutation<
      Blob,
      { matterId: string; blob: BlobUpdateRequest; blobId: string }
    >({
      query: ({ matterId, blob, blobId }) => ({
        url: `api/matters/${matterId}/blobs/${blobId}/`,
        method: 'PUT',
        body: blob,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Blobs', id: matterId },
      ],
    }),
    getBlobDetail: builder.query<
      BlobDetail,
      { matterId: string; blobId: string }
    >({
      query: ({ matterId, blobId }) => ({
        url: `api/matters/${matterId}/blobs/${blobId}/details/`,
        method: 'GET',
      }),
      providesTags: (result, error, { matterId, blobId }) => [
        { type: 'BlobDetail', id: `${matterId}-${blobId}` },
      ],
    }),
    getBlobWithMarkdownContent: builder.query<
      BlobWithMarkdownContent,
      { matterId: string; blobId: string }
    >({
      query: ({ matterId, blobId }) => ({
        url: `api/matters/${matterId}/blobs/${blobId}/markdown/`,
        method: 'GET',
      }),
      providesTags: (result, error, { matterId, blobId }) => [
        { type: 'BlobDetail', id: `${matterId}-${blobId}` },
      ],
    }),
    initiateMultipart: builder.mutation<
      InitiateMultipartResponse,
      { matterId: string; body: InitiateMultipartPayload }
    >({
      query: ({ matterId, body }) => ({
        url: `api/matters/${matterId}/blobs/multipart/initiate/`,
        method: 'POST',
        body,
      }),
    }),

    presignPart: builder.mutation<
      PresignPartResponse,
      { matterId: string; body: PresignPartPayload }
    >({
      query: ({ matterId, body }) => ({
        url: `api/matters/${matterId}/blobs/multipart/parts/`,
        method: 'POST',
        body,
      }),
    }),

    completeMultipart: builder.mutation<
      Blob, // back end returns full Blob object
      { matterId: string; body: CompleteMultipartPayload }
    >({
      query: ({ matterId, body }) => ({
        url: `api/matters/${matterId}/blobs/multipart/complete/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (r, e, { matterId }) => [
        { type: 'Blobs', id: matterId },
      ],
    }),

    // Unified upload endpoints (backend determines presigned vs multipart based on file_size)
    initiateUpload: builder.mutation<
      InitiateUploadResponse,
      { matterId: string; body: InitiateUploadPayload }
    >({
      query: ({ matterId, body }) => ({
        url: `api/matters/${matterId}/blobs/upload/presign/initiate/`,
        method: 'POST',
        body,
      }),
    }),

    completePresignedUpload: builder.mutation<
      Blob,
      { matterId: string; body: CompletePresignedUploadPayload }
    >({
      query: ({ matterId, body }) => ({
        url: `api/matters/${matterId}/blobs/upload/presign/complete/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (r, e, { matterId }) => [
        { type: 'Blobs', id: matterId },
      ],
    }),

    retriggerBlobTransform: builder.mutation<
      { status: string },
      { matterId: string; blobId: string }
    >({
      query: ({ matterId, blobId }) => ({
        url: `api/matters/${matterId}/blobs/${blobId}/transform/retrigger/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, blobId }) => [
        { type: 'BlobDetail', id: `${matterId}-${blobId}` },
      ],
    }),
    copyBlobsToCompanions: builder.mutation<
      CopyBlobsToCompanionsResponse,
      { matterId: string; body: CopyBlobsToCompanionsRequest }
    >({
      query: ({ matterId, body }) => ({
        url: `api/matters/${matterId}/blobs/copy-to-companions/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { matterId }) =>
        result && !result.dry_run ? [{ type: 'Blobs', id: matterId }] : [],
    }),
    downloadBulk: builder.mutation<
      { blob: globalThis.Blob; filename: string },
      { matterId: string; body: BulkDownloadRequest }
    >({
      query: ({ matterId, body }) => ({
        url: `api/matters/${matterId}/blobs/download-bulk/`,
        method: 'POST',
        body,
        cache: 'no-store',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const cd =
            response.headers.get('Content-Disposition') ||
            response.headers.get('content-disposition') ||
            '';
          const match = cd.match(/filename="?([^";]+)"?/i) ?? [];
          const [, parsedName] = match as string[];
          const filename = parsedName || 'documents.zip';
          return { blob, filename };
        },
      }),
    }),
    moveBlobToMatter: builder.mutation<
      Blob,
      { matterId: string; blobId: string; body: MoveBlobToMatterRequest }
    >({
      query: ({ matterId, blobId, body }) => ({
        url: `api/matters/${matterId}/blobs/${blobId}/move-to-matter/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { matterId, body }) => [
        { type: 'Blobs', id: matterId },
        { type: 'Blobs', id: body.target_matter_id },
      ],
    }),
    extractPages: builder.mutation<
      ExtractPagesResponse,
      { matterId: string; blobId: string; body: ExtractPagesRequest }
    >({
      query: ({ matterId, blobId, body }) => ({
        url: `api/matters/${matterId}/blobs/${blobId}/extract-pages/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Blobs', id: matterId },
      ],
    }),
    bulkResort: builder.mutation<
      BulkReSortInitResponse,
      { matterId: string; body: BulkResortRequest }
    >({
      query: ({ matterId, body }) => ({
        url: `api/matters/${matterId}/blobs/bulk-resort/`,
        method: 'POST',
        body,
      }),
      // Don't invalidate immediately - the operation is async
      // Progress is shown in WorkflowIndicator
    }),
    getBulkReSortOperationStatus: builder.query<
      BulkReSortOperationSchema,
      { matterId: string; operationId: string }
    >({
      query: ({ matterId, operationId }) => ({
        url: `api/matters/${matterId}/blobs/bulk-resort-operations/${operationId}/`,
        method: 'GET',
      }),
    }),
    getActiveBulkReSortOperations: builder.query<
      BulkReSortOperationSchema[],
      { matterId: string }
    >({
      query: ({ matterId }) => ({
        url: `api/matters/${matterId}/blobs/bulk-resort-operations/`,
        method: 'GET',
      }),
    }),
    getSortingRules: builder.query<SortingRulesResponse, { matterId: string }>({
      query: ({ matterId }) => ({
        url: `api/matters/${matterId}/blobs/sorting-rules/`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetBlobsQuery,
  useLazyGetBlobsQuery,
  useCreateBlobMutation,
  useDestroyBlobMutation,
  useUpdateBlobMutation,
  useGetBlobDetailQuery,
  useLazyGetBlobDetailQuery,
  useGetBlobWithMarkdownContentQuery,
  useInitiateMultipartMutation,
  usePresignPartMutation,
  useCompleteMultipartMutation,
  useInitiateUploadMutation,
  useCompletePresignedUploadMutation,
  useRetriggerBlobTransformMutation,
  useCopyBlobsToCompanionsMutation,
  useDownloadBulkMutation,
  useMoveBlobToMatterMutation,
  useExtractPagesMutation,
  useBulkResortMutation,
  useGetBulkReSortOperationStatusQuery,
  useLazyGetBulkReSortOperationStatusQuery,
  useGetActiveBulkReSortOperationsQuery,
  useGetSortingRulesQuery,
  useLazyGetSortingRulesQuery,
} = blobsApiClient;
